/* ==========================================================================
   Wontek — global.js
   Vanilla, no dependencies. Everything is delegated so sections re-rendered
   by the Section Rendering API keep working without re-binding.
   ========================================================================== */

(function () {
  'use strict';

  const money = (cents) =>
    (window.Shopify && window.Shopify.formatMoney)
      ? window.Shopify.formatMoney(cents)
      : (cents / 100).toFixed(2);

  /* ---- Cart drawer ------------------------------------------------------ */

  const drawer = () => document.querySelector('[data-cart-drawer]');
  const overlay = () => document.querySelector('[data-overlay]');

  function openCart() {
    closeMobileNav();
    closePanels();
    const d = drawer();
    const o = overlay();
    if (!d) return;
    d.setAttribute('open', '');
    d.setAttribute('aria-hidden', 'false');
    if (o) {
      o.hidden = false;
      o.dataset.open = 'true';
    }
    document.body.style.overflow = 'hidden';
    const close = d.querySelector('[data-cart-close]');
    if (close) close.focus();
  }

  function closeCart() {
    const d = drawer();
    const o = overlay();
    if (!d) return;
    d.removeAttribute('open');
    d.setAttribute('aria-hidden', 'true');
    if (o) {
      o.dataset.open = 'false';
      setTimeout(() => { o.hidden = true; }, 300);
    }
    document.body.style.overflow = '';
  }

  /* ---- Mobile nav ------------------------------------------------------- */

  const mobileNav = () => document.querySelector('[data-mobile-nav]');

  const mobileNavOpen = () => { const n = mobileNav(); return n && !n.hidden; };
  const cartOpen = () => { const d = drawer(); return d && d.hasAttribute('open'); };

  function openMobileNav(mode) {
    const nav = mobileNav();
    if (!nav) return;
    closePanels();
    nav.dataset.mode = mode === 'search' ? 'search' : 'menu';
    nav.hidden = false;
    // Next frame, so the transition has a from-state to animate out of.
    requestAnimationFrame(() => nav.setAttribute('open', ''));
    document.body.style.overflow = 'hidden';
    document.querySelectorAll('[data-mobile-nav-open]').forEach((b) => b.setAttribute('aria-expanded', 'true'));
    if (nav.dataset.mode === 'search') {
      const input = nav.querySelector('[data-search-input]');
      if (input) setTimeout(() => input.focus(), 50);
    } else {
      const close = nav.querySelector('[data-mobile-nav-close]');
      if (close) close.focus();
    }
  }

  function closeMobileNav() {
    const nav = mobileNav();
    if (!nav || nav.hidden) return;
    nav.removeAttribute('open');
    document.body.style.overflow = '';
    document.querySelectorAll('[data-mobile-nav-open]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
    setTimeout(() => { nav.hidden = true; }, 300);
  }

  function selectMobileTab(button) {
    const nav = mobileNav();
    if (!nav) return;
    const targetId = button.dataset.mobileTab;
    nav.querySelectorAll('[data-mobile-tab]').forEach((b) => {
      b.setAttribute('aria-selected', String(b === button));
    });
    nav.querySelectorAll('[data-mobile-panel]').forEach((panel) => {
      panel.hidden = panel.id !== targetId;
    });
  }

  /* ---- Header sheet: mega menu panels + search state --------------------
     Opening a nav item or Search turns the ribbon into the maroon sheet
     (Menu / Variant2–4 in Figma). One panel is open at a time. */

  const header = () => document.querySelector('[data-header]');

  function closePanels() {
    const h = header();
    if (!h) return;
    h.querySelectorAll('[data-panel]').forEach((p) => { p.hidden = true; p.removeAttribute('open'); });
    h.querySelectorAll('[data-panel-toggle], [data-search-open]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
    h.classList.remove('header--open', 'header--search');
    if (!mobileNavOpen() && !cartOpen()) document.body.style.overflow = '';
  }

  function openPanel(id, toggle) {
    const h = header();
    const panel = document.getElementById(id);
    if (!h || !panel) return;
    closePanels();
    panel.hidden = false;
    panel.setAttribute('open', '');
    if (toggle) toggle.setAttribute('aria-expanded', 'true');
    h.classList.add('header--open');
    if (panel.hasAttribute('data-search-panel')) h.classList.add('header--search');
  }

  /* ---- Search ------------------------------------------------------------ */

  const isMobile = () => window.matchMedia('(max-width: 989px)').matches;

  function openSearch() {
    if (isMobile()) {
      openMobileNav('search');
      return;
    }
    const h = header();
    const trigger = h && h.querySelector('[data-search-open]');
    openPanel('PanelSearch', trigger);
    const input = document.querySelector('#PanelSearch [data-search-input]');
    if (input) input.focus();
  }

  function closeSearch() {
    const h = header();
    if (h && h.classList.contains('header--search')) closePanels();
  }

  /* ---- Cart operations -------------------------------------------------- */

  async function refreshCart() {
    try {
      const res = await fetch(window.Shopify.routes.root + '?section_id=cart-drawer');
      const html = await res.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');

      const newBody = parsed.querySelector('[data-cart-body]');
      const oldBody = document.querySelector('[data-cart-body]');
      if (newBody && oldBody) oldBody.innerHTML = newBody.innerHTML;

      const newFooter = parsed.querySelector('[data-cart-footer]');
      const oldFooter = document.querySelector('[data-cart-footer]');
      if (oldFooter && newFooter) {
        oldFooter.innerHTML = newFooter.innerHTML;
      } else if (oldFooter && !newFooter) {
        oldFooter.remove();
      } else if (!oldFooter && newFooter) {
        const d = drawer();
        if (d) d.appendChild(newFooter);
      }

      const cartRes = await fetch(window.Shopify.routes.root + 'cart.js');
      const cart = await cartRes.json();
      updateCartCount(cart.item_count);
    } catch (e) {
      console.error('Cart refresh failed', e);
    }
  }

  function updateCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }

  async function changeLine(key, quantity) {
    const res = await fetch(window.Shopify.routes.root + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity })
    });
    if (!res.ok) throw new Error('Cart change failed');
    return res.json();
  }

  async function addToCart(formData) {
    const res = await fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      body: formData
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.description || data.message || 'Add to cart failed');
    return data;
  }

  /* ---- Carousel --------------------------------------------------------- */

  function carouselStep(track) {
    const item = track.querySelector('.carousel__item') || track.firstElementChild;
    if (!item) return track.clientWidth * 0.8;
    // Read the real gap — it differs per breakpoint, and a hard-coded 16 drifts.
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    return item.offsetWidth + gap;
  }

  function scrollCarousel(root, direction) {
    const track = root.querySelector('[data-carousel]');
    if (!track) return;
    track.scrollBy({ left: carouselStep(track) * direction, behavior: 'smooth' });
  }

  function syncCarouselControls(root) {
    const track = root.querySelector('[data-carousel]');
    if (!track) return;

    /* Nothing to page through — hide the controls rather than showing two
       dead buttons. Re-checked on scroll, resize and whenever the track
       changes size (images finishing, fonts swapping). */
    const overflow = track.scrollWidth - track.clientWidth;
    const scrollable = overflow > 1;
    root.querySelectorAll('.carousel__controls').forEach((controls) => {
      controls.hidden = !scrollable;
    });
    if (!scrollable) return;

    const prev = root.querySelector('[data-carousel-prev]');
    const next = root.querySelector('[data-carousel-next]');
    if (prev) prev.disabled = track.scrollLeft <= 0;
    if (next) next.disabled = track.scrollLeft >= overflow - 1;
  }

  /* Heritage carousel: the card nearest the left edge is "current", the
     rest sit at 40% as on both boards. */
  function syncCarouselDim(track) {
    if (!track.hasAttribute('data-carousel-dim')) return;
    const items = Array.from(track.querySelectorAll('.carousel__item'));
    if (!items.length) return;
    const left = track.scrollLeft;
    let current = items[0];
    let best = Infinity;
    items.forEach((item) => {
      const d = Math.abs(item.offsetLeft - track.offsetLeft - left);
      if (d < best) { best = d; current = item; }
    });
    items.forEach((item) => { item.dataset.dim = String(item !== current); });
  }

  function initCarousels() {
    document.querySelectorAll('[data-carousel-root]').forEach((root) => {
      const track = root.querySelector('[data-carousel]');
      if (!track || track.dataset.carouselBound === 'true') return;
      track.dataset.carouselBound = 'true';
      syncCarouselControls(root);
      syncCarouselDim(track);
      track.addEventListener('scroll', () => { syncCarouselControls(root); syncCarouselDim(track); }, { passive: true });
      window.addEventListener('resize', () => { syncCarouselControls(root); syncCarouselDim(track); });

      /* Cards start as empty boxes and grow as their images arrive, so the
         first measurement can be wrong. Watch the track instead of guessing. */
      if (window.ResizeObserver) {
        const ro = new ResizeObserver(() => { syncCarouselControls(root); syncCarouselDim(track); });
        ro.observe(track);
        if (track.firstElementChild) ro.observe(track.firstElementChild);
      }
    });
  }

  /* ---- Heritage hotspots -------------------------------------------------
     Hovering or keyboard-focusing a hotspot on the feature image highlights
     the product it points at in the mini carousel. Hotspot and card are paired
     by the Liquid block id (data-hotspot / data-hotspot-item) rather than by
     position, because hotspots with no product are skipped in the carousel.

     Delegated from document and driven by a single `active` reference, so the
     common case (mouse moving over anything that is not a hotspot) costs one
     closest() and an early return, and a section re-render needs no rebinding.
     --------------------------------------------------------------------- */

  let activeHotspot = null;

  function heritageCardFor(hotspot) {
    const root = hotspot.closest('[data-heritage]');
    if (!root) return null;
    const id = hotspot.getAttribute('data-hotspot');
    if (!id) return null;
    /* Compared as strings rather than via a selector: a block id is not
       guaranteed to be a valid CSS identifier. */
    const card = Array.prototype.find.call(
      root.querySelectorAll('[data-hotspot-item]'),
      (el) => el.getAttribute('data-hotspot-item') === id
    );
    return card ? { root, card } : null;
  }

  function clearHotspot() {
    if (!activeHotspot) return;
    const match = heritageCardFor(activeHotspot);
    activeHotspot = null;
    if (!match) return;
    match.root.removeAttribute('data-hotspot-active');
    match.root.querySelectorAll('[data-hotspot-match]').forEach((el) => {
      el.removeAttribute('data-hotspot-match');
    });
  }

  function syncHotspot(event) {
    const hotspot = event.target.closest ? event.target.closest('[data-hotspot]') : null;
    if (hotspot === activeHotspot) return;
    clearHotspot();
    if (!hotspot) return;
    const match = heritageCardFor(hotspot);
    /* A hotspot with no product has no card to highlight — leave the row alone
       rather than dimming every card and lighting none of them. */
    if (!match) return;
    activeHotspot = hotspot;
    match.root.setAttribute('data-hotspot-active', '');
    match.card.setAttribute('data-hotspot-match', '');
  }

  document.addEventListener('mouseover', syncHotspot);
  document.addEventListener('focusin', syncHotspot);
  /* Pointer leaving the window fires no further mouseover, so drop the state. */
  document.addEventListener('mouseleave', clearHotspot);

  /* ---- Predictive search ------------------------------------------------ */

  let searchTimer;

  async function runSearch(query, resultsEl) {
    if (!query || query.length < 2) {
      resultsEl.innerHTML = '';
      return;
    }
    try {
      const url = window.Shopify.routes.root +
        'search/suggest?q=' + encodeURIComponent(query) +
        '&resources[type]=product,collection,article&resources[limit]=6&section_id=predictive-search';
      const res = await fetch(url);
      const html = await res.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const results = parsed.querySelector('[data-search-results]');
      resultsEl.innerHTML = results ? results.innerHTML : '';
    } catch (e) {
      console.error('Search failed', e);
    }
  }

  /* ---- Delegated events ------------------------------------------------- */

  document.addEventListener('click', async (event) => {
    const t = event.target;

    if (t.closest('[data-cart-open]')) { event.preventDefault(); openCart(); return; }
    if (t.closest('[data-cart-close]') || t.closest('[data-overlay]')) { closeCart(); return; }

    const searchOpen = t.closest('[data-search-open]');
    if (searchOpen) {
      event.preventDefault();
      if (searchOpen.getAttribute('aria-expanded') === 'true') closeSearch(); else openSearch();
      return;
    }
    if (t.closest('[data-search-close]')) { closePanels(); return; }

    const clear = t.closest('[data-search-clear]');
    if (clear) {
      const form = clear.closest('form');
      const input = form && form.querySelector('[data-search-input]');
      if (input) { input.value = ''; input.dispatchEvent(new Event('input', { bubbles: true })); input.focus(); }
      return;
    }

    if (t.closest('[data-mobile-nav-open]')) { event.preventDefault(); openMobileNav('menu'); return; }
    if (t.closest('[data-mobile-search]')) { event.preventDefault(); openMobileNav('search'); return; }
    if (t.closest('[data-mobile-nav-close]')) { event.preventDefault(); closeMobileNav(); return; }

    const mobileTab = t.closest('[data-mobile-tab]');
    if (mobileTab) { event.preventDefault(); selectMobileTab(mobileTab); return; }

    const panelToggle = t.closest('[data-panel-toggle]');
    if (panelToggle) {
      const isOpen = panelToggle.getAttribute('aria-expanded') === 'true';
      if (isOpen) closePanels(); else openPanel(panelToggle.dataset.panelToggle, panelToggle);
      return;
    }

    // Click outside the header closes any open sheet.
    const h = header();
    if (h && h.classList.contains('header--open') && !t.closest('[data-header]')) { closePanels(); }

    const prev = t.closest('[data-carousel-prev]');
    const next = t.closest('[data-carousel-next]');
    if (prev || next) {
      const root = (prev || next).closest('[data-carousel-root]');
      if (root) scrollCarousel(root, prev ? -1 : 1);
      return;
    }

    const qtyStep = t.closest('[data-qty-up], [data-qty-down]');
    if (qtyStep) {
      const wrap = qtyStep.closest('.quantity');
      const input = wrap && wrap.querySelector('input');
      if (input) {
        const delta = qtyStep.hasAttribute('data-qty-up') ? 1 : -1;
        input.value = Math.max(1, (parseInt(input.value, 10) || 1) + delta);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }

    const lineStep = t.closest('[data-line-qty]');
    if (lineStep) {
      const line = lineStep.closest('[data-cart-line]');
      const input = line && line.querySelector('[data-line-qty-input]');
      if (line && input) {
        const delta = parseInt(lineStep.dataset.lineQty, 10);
        const next = Math.max(0, (parseInt(input.value, 10) || 0) + delta);
        input.value = next;
        try {
          await changeLine(line.dataset.lineKey, next);
          await refreshCart();
        } catch (e) { console.error(e); }
      }
      return;
    }

    const remove = t.closest('[data-line-remove]');
    if (remove) {
      const line = remove.closest('[data-cart-line]');
      if (line) {
        try {
          await changeLine(line.dataset.lineKey, 0);
          await refreshCart();
          if (document.body.classList.contains('template-cart')) window.location.reload();
        } catch (e) { console.error(e); }
      }
      return;
    }
  });

  document.addEventListener('change', async (event) => {
    const input = event.target.closest('[data-line-qty-input]');
    if (input) {
      const line = input.closest('[data-cart-line]');
      if (line) {
        try {
          await changeLine(line.dataset.lineKey, Math.max(0, parseInt(input.value, 10) || 0));
          await refreshCart();
        } catch (e) { console.error(e); }
      }
      return;
    }

    const sort = event.target.closest('[data-facet-form] select, [data-facet-form] input');
    if (sort) {
      const form = sort.closest('[data-facet-form]');
      if (form) {
        const params = new URLSearchParams(new FormData(form));
        for (const [k, v] of [...params.entries()]) if (!v) params.delete(k);
        window.location.search = params.toString();
      }
    }
  });

  document.addEventListener('input', (event) => {
    const search = event.target.closest('[data-search-input]');
    if (!search) return;
    const scope = search.closest('[data-search-panel], [data-mobile-nav]') || document;
    const results = scope.querySelector('[data-search-results-mount]');
    if (!results) return;
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => runSearch(search.value, results), 250);
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!form.querySelector('[data-add-to-cart]')) return;

    event.preventDefault();
    const button = form.querySelector('[data-add-to-cart]');
    const label = form.querySelector('[data-add-to-cart-text]');
    const errorEl = form.querySelector('[data-cart-error]');
    const original = label ? label.textContent : '';

    button.disabled = true;
    if (label) label.textContent = '…';
    if (errorEl) errorEl.hidden = true;

    try {
      await addToCart(new FormData(form));
      await refreshCart();
      openCart();
    } catch (e) {
      if (errorEl) {
        errorEl.textContent = e.message;
        errorEl.hidden = false;
      }
    } finally {
      button.disabled = false;
      if (label) label.textContent = original;
    }
  });

  /* ---- Localization form ------------------------------------------------
     Changing the language or country select submits its form, so the visible
     Update button is only needed without JS (base.css hides it under .js).
     Delegated, so a section re-render keeps it working.
     --------------------------------------------------------------------- */

  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-localization-select]');
    if (!select) return;
    const form = select.form;
    if (!form) return;
    select.disabled = false;
    form.submit();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') { closeCart(); closePanels(); closeMobileNav(); }
  });

  initCarousels();
  document.addEventListener('shopify:section:load', initCarousels);

  /* ---- Theme editor ------------------------------------------------------
     Menu tile blocks live inside a closed mega-menu panel, so selecting one in
     the customizer would otherwise change nothing on screen. Open its panel.
     The mobile copy of the same block is not inside [data-panel], so it is
     ignored and only the desktop sheet opens. */
  document.addEventListener('shopify:block:select', (event) => {
    const panel = event.target.closest && event.target.closest('[data-panel]');
    if (!panel || !panel.id) return;
    openPanel(panel.id, document.querySelector(`[data-panel-toggle="${panel.id}"]`));
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    if (event.target.closest && event.target.closest('[data-panel]')) closePanels();
  });

  window.wontek = { openCart, closeCart, openMobileNav, closeMobileNav, openSearch, closeSearch, openPanel, closePanels, refreshCart, updateCartCount, initCarousels, money };
})();
